import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../navigation/AuthProvider";
import { Toast } from "../../components/ToastPopup";
import { ROLE_LABELS_SV } from "../../permissions/roleLabels";
import {
  get_history_access,
  list_history,
  get_history_record,
  get_shift_history,
  get_history_people,
  set_history_access,
} from "../../utils/historyRequests";
import { rememberHistoryAccess } from "../../utils/useHistoryAccess";
import "../css/History.css";

// ───────────────────────── Hjälpare ─────────────────────────

const ACTION_OPTIONS = [
  ["shift_created", "Skapade pass"],
  ["shift_edited", "Ändrade pass"],
  ["shift_deleted", "Tog bort pass"],
  ["shift_restored", "Återställde pass"],
  ["shifts_copied", "Kopierade vecka"],
  ["shifts_imported", "Importerade pass"],
  ["schedule_published", "Publicerade schema"],
  ["schedule_recalled", "Återkallade schema"],
  ["payroll_edited", "Ändrade löneunderlag"],
  ["absence_approved", "Godkände frånvaro"],
  ["absence_reverted", "Ångrade frånvaro"],
  ["overtime_approved", "Godkände övertid"],
  ["overtime_reverted", "Ångrade övertid"],
  ["application_approved", "Godkände passansökan"],
  ["application_revoked", "Återkallade passansökan"],
  ["employee_removed", "Tog bort anställd"],
  ["history_access_changed", "Ändrade historikåtkomst"],
];

const pad = (n) => String(n).padStart(2, "0");
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toYmd(d);
};

const timeOf = (iso) => new Date(iso).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });

const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date();
  y.setDate(today.getDate() - 1);
  if (toYmd(d) === toYmd(today)) return "Idag";
  if (toYmd(d) === toYmd(y)) return "Igår";
  return d.toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

const initials = (name) => {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const EMPTY_FILTERS = {
  dateMode: "changed",
  from: "",
  to: "",
  performedBy: "",
  employeeId: "",
  projectId: "",
  timeSchemaId: "",
  action: "",
};

// Passets id för "Passets historik" – nya poster har det på passet, äldre i entityId
const shiftIdOf = (r) => {
  if (r.shiftCount === 1 && r.shifts && r.shifts[0] && r.shifts[0].shiftId) return r.shifts[0].shiftId;
  if (r.legacy && r.entityType === "Shift") return r.entityId;
  return null;
};

// ───────────────────────── Små komponenter ─────────────────────────

// Meningen, med personens namn i fetstil först, t.ex. "**Anna** ändrade pass (…) → …"
const Sentence = ({ r }) => {
  const name = r.performedByName;
  if (name && r.message.startsWith(name)) {
    return (
      <>
        <strong>{name}</strong>
        {r.message.slice(name.length)}
      </>
    );
  }
  return <>{r.message}</>;
};

// Ett pass som en enkel textrad (används när en stor ändring fälls ut)
const shiftText = (s) =>
  [s.date, `${s.startTime || "?"}–${s.endTime || "?"}`, s.projectName, s.timeSchemaName, (s.employeeNames || []).join(", ")]
    .filter(Boolean)
    .join(" · ");

const RecordRow = ({ r, expanded, onOpenShift, onShowAll }) => {
  const sid = shiftIdOf(r);
  const shifts = expanded ? expanded.shifts : [];

  return (
    <>
      <p className="hs-sentence"><Sentence r={r} /></p>

      {sid && (
        <button type="button" className="hs-link" onClick={() => onOpenShift(sid)}>
          Passets historik
        </button>
      )}

      {r.shiftCount > 1 && !expanded && (
        <button type="button" className="hs-link" onClick={() => onShowAll(r)}>
          Visa alla {r.shiftCount} pass
        </button>
      )}

      {expanded && (
        <ul className="hs-shiftlist">
          {shifts.map((s, i) => (
            <li key={(s.shiftId || "x") + i}>
              {shiftText(s)}
              {s.shiftId && (
                <button type="button" className="hs-link hs-inline" onClick={() => onOpenShift(s.shiftId)}>
                  Passets historik
                </button>
              )}
            </li>
          ))}
          {r.shiftCount > shifts.length && <li className="hs-note">Visar de första {shifts.length} av {r.shiftCount} pass.</li>}
        </ul>
      )}
    </>
  );
};

// ───────────────────────── Passets historik (sidopanel) ─────────────────────────

const ShiftDrawer = ({ shiftId, user, company, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError("");
    get_shift_history({ user, company, shiftId })
      .then((d) => {
        if (cancelled) return;
        if (d.status === "success") setData(d);
        else setError(d.errorText || "Kunde inte hämta passets historik");
      })
      .catch(() => !cancelled && setError("Kunde inte hämta passets historik"));
    return () => {
      cancelled = true;
    };
  }, [shiftId, user, company]);

  const cur = data && data.current;

  return (
    <div className="hs-overlay" onClick={onClose}>
      <aside className="hs-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Passets historik">
        <div className="hs-drawer-head">
          <h2>Passets historik</h2>
          <button type="button" className="hs-x" onClick={onClose} aria-label="Stäng">×</button>
        </div>

        {error && <p className="hs-error">{error}</p>}
        {!data && !error && <p className="hs-muted">Laddar…</p>}

        {data && (
          <>
            {cur ? (
              <div className="hs-current">
                <div className="hs-current-title">
                  {cur.date} · {cur.startTime || "?"}–{cur.endTime || "?"}
                  {cur.isDeleted && <span className="hs-deleted">Borttaget</span>}
                </div>
                <div className="hs-muted">
                  {[cur.projectName, cur.timeSchemaName, (cur.employeeNames || []).join(", ")].filter(Boolean).join(" · ") || "Ingen information"}
                </div>
              </div>
            ) : (
              <div className="hs-current"><div className="hs-muted">Passet finns inte längre.</div></div>
            )}

            {data.records.length === 0 ? (
              <p className="hs-muted">Ingen historik finns för det här passet.</p>
            ) : (
              <ol className="hs-timeline">
                {data.records.map((r) => (
                  <li key={r.id} className="hs-tl-item">
                    <span className="hs-dot" />
                    <div className="hs-time">{new Date(r.createdAt).toLocaleString("sv-SE", { dateStyle: "medium", timeStyle: "short" })}</div>
                    <p className="hs-sentence"><Sentence r={r} /></p>
                    {r.batchSize > 1 && <div className="hs-note">Ingick i en ändring av {r.batchSize} pass.</div>}
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
      </aside>
    </div>
  );
};

// ───────────────────────── Åtkomst (superadmin) ─────────────────────────

const AccessPanel = ({ user, company, showToast }) => {
  const [people, setPeople] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    get_history_people({ user, company })
      .then((d) => {
        if (cancelled) return;
        if (d.status === "success") setPeople(d.people);
        else setError(d.errorText || "Kunde inte hämta personer");
      })
      .catch(() => !cancelled && setError("Kunde inte hämta personer"));
    return () => {
      cancelled = true;
    };
  }, [user, company]);

  const toggle = async (p) => {
    if (p.isSuperAdmin || busyId) return;
    const allowed = !p.hasAccess;
    setBusyId(p.userId);
    setPeople((list) => list.map((x) => (x.userId === p.userId ? { ...x, hasAccess: allowed } : x)));
    try {
      const d = await set_history_access({ user, company, targetUserId: p.userId, allowed });
      if (d.status !== "success") throw new Error(d.errorText);
      showToast(allowed ? `${p.name} kan nu se historiken` : `${p.name} kan inte längre se historiken`);
    } catch (e) {
      setPeople((list) => list.map((x) => (x.userId === p.userId ? { ...x, hasAccess: !allowed } : x)));
      showToast((e && e.message) || "Kunde inte ändra åtkomsten", "error");
    }
    setBusyId(null);
  };

  return (
    <div className="hs-card">
      <div className="hs-card-head">
        <h2>Vem får se historiken?</h2>
        <p>
          Du som superadmin har alltid åtkomst. Välj vilka andra som ska kunna se vem som ändrat pass.
          Ändringen gäller direkt och sparas automatiskt.
        </p>
      </div>

      {error && <p className="hs-error hs-pad">{error}</p>}
      {!people && !error && <p className="hs-muted hs-pad">Laddar…</p>}

      {people && people.length === 0 && <p className="hs-muted hs-pad">Det finns inga personer med webbinloggning.</p>}

      {people && people.map((p) => (
        <div className="hs-person" key={p.userId}>
          <div className="hs-avatar">{initials(p.name)}</div>
          <div className="hs-person-info">
            <div className="hs-person-name">{p.name || p.email}</div>
            <div className="hs-muted">{ROLE_LABELS_SV[p.role] || p.role}{p.email ? ` · ${p.email}` : ""}</div>
          </div>
          {p.isSuperAdmin ? (
            <span className="hs-always">Alltid åtkomst</span>
          ) : (
            <label className="hs-switch" title={p.hasAccess ? "Har åtkomst" : "Har inte åtkomst"}>
              <input
                type="checkbox"
                role="switch"
                aria-label={`Åtkomst till historik för ${p.name}`}
                checked={p.hasAccess}
                disabled={busyId === p.userId}
                onChange={() => toggle(p)}
              />
              <span className="hs-slider" />
            </label>
          )}
        </div>
      ))}
    </div>
  );
};

// ───────────────────────── Sidan ─────────────────────────

export const History = () => {
  const { user, company, employees, projects, timeSchemas } = useAuth();

  const [access, setAccess] = useState({ loading: true, canView: false, isSuperAdmin: false });
  const [tab, setTab] = useState("changes");
  const [toast, setToast] = useState({ message: "", type: "success" });
  const showToast = (message, type = "success") => setToast({ message, type });

  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);

  const [records, setRecords] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextBeforeId, setNextBeforeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [expandedRecords, setExpandedRecords] = useState({});
  const [openShiftId, setOpenShiftId] = useState(null);
  const seq = useRef(0);

  const userId = user && user._id;
  const companyId = company && company._id;

  // Åtkomst
  useEffect(() => {
    if (!userId || !companyId) return;
    let cancelled = false;
    get_history_access({ user, company })
      .then((d) => {
        if (cancelled) return;
        const value =
          d && d.status === "success"
            ? { loading: false, canView: !!d.canView, isSuperAdmin: !!d.isSuperAdmin }
            : { loading: false, canView: false, isSuperAdmin: false };
        rememberHistoryAccess(user, company, value);
        setAccess(value);
      })
      .catch(() => !cancelled && setAccess({ loading: false, canView: false, isSuperAdmin: false }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, companyId]);

  // Fördröj sökningen så att vi inte frågar servern vid varje tangenttryck
  useEffect(() => {
    const t = setTimeout(() => setAppliedQuery(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  const apiFilters = useMemo(() => {
    const f = { ...filters, q: appliedQuery };
    Object.keys(f).forEach((k) => f[k] === "" && delete f[k]);
    return f;
  }, [filters, appliedQuery]);

  const load = useCallback(
    async ({ append = false, beforeId = null } = {}) => {
      if (!userId || !companyId) return;
      const mySeq = ++seq.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const d = await list_history({ user, company, filters: apiFilters, beforeId, limit: 50 });
        if (mySeq !== seq.current) return; // ett nyare sökande har startat
        if (d.status === "success") {
          setRecords((prev) => (append ? [...prev, ...d.records] : d.records));
          setHasMore(!!d.hasMore);
          setNextBeforeId(d.nextBeforeId || null);
          setError("");
        } else {
          setError(d.errorText || "Kunde inte hämta historiken");
        }
      } catch (e) {
        if (mySeq === seq.current) setError("Kunde inte hämta historiken");
      }
      if (mySeq === seq.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, companyId, apiFilters]
  );

  useEffect(() => {
    if (access.canView && tab === "changes") {
      setExpandedRecords({});
      load();
    }
  }, [access.canView, tab, load]);

  const setFilter = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => k !== "dateMode" && v !== "").length;
  const anythingActive = activeFilterCount > 0 || appliedQuery !== "";

  const clearAll = () => {
    setFilters(EMPTY_FILTERS);
    setQuery("");
    setAppliedQuery("");
  };

  const preset = (days) => setFilter({ dateMode: "changed", from: daysAgo(days), to: daysAgo(0) });

  const showAll = async (r) => {
    try {
      const d = await get_history_record({ user, company, recordId: r.id });
      if (d.status === "success") setExpandedRecords((prev) => ({ ...prev, [r.id]: d.record }));
      else showToast(d.errorText || "Kunde inte hämta passen", "error");
    } catch (e) {
      showToast("Kunde inte hämta passen", "error");
    }
  };

  // Gruppera per dag
  const groups = useMemo(() => {
    const out = [];
    for (const r of records) {
      const label = dayLabel(r.createdAt);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(r);
      else out.push({ label, items: [r] });
    }
    return out;
  }, [records]);

  const employeeList = Array.isArray(employees) ? employees : [];
  const projectList = Array.isArray(projects) ? projects : [];
  const schemaList = Array.isArray(timeSchemas) ? timeSchemas : [];

  // ───── Render ─────
  if (access.loading) {
    return (
      <div className="hs-root">
        <div className="hs-container"><p className="hs-muted">Laddar…</p></div>
      </div>
    );
  }

  if (!access.canView) {
    return (
      <div className="hs-root">
        <div className="hs-container">
          <div className="hs-card hs-locked">
            <div className="hs-lock">🔒</div>
            <h2>Du har inte åtkomst till historiken</h2>
            <p>Be en superadmin i ditt företag om åtkomst.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hs-root">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="hs-container">
        <div className="hs-header">
          <div>
            <h1>Historik</h1>
            <p className="hs-subhead">Se vem som har ändrat i schemat, när och vad som ändrades.</p>
          </div>
        </div>

        {access.isSuperAdmin && (
          <div className="hs-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === "changes"} className={tab === "changes" ? "on" : ""} onClick={() => setTab("changes")}>
              Ändringar
            </button>
            <button type="button" role="tab" aria-selected={tab === "access"} className={tab === "access" ? "on" : ""} onClick={() => setTab("access")}>
              Åtkomst
            </button>
          </div>
        )}

        {tab === "access" && access.isSuperAdmin ? (
          <AccessPanel user={user} company={company} showToast={showToast} />
        ) : (
          <>
            {/* Sök + filter */}
            <div className="hs-card hs-searchcard">
              <div className="hs-searchrow">
                <input
                  className="hs-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Sök på namn, projekt, datum eller tid – t.ex. ”anna 2026-10-05”"
                  aria-label="Sök i historiken"
                />
                <button type="button" className={`hs-filterbtn ${showFilters || activeFilterCount ? "on" : ""}`} onClick={() => setShowFilters((v) => !v)}>
                  Filter{activeFilterCount ? ` (${activeFilterCount})` : ""}
                </button>
              </div>

              {showFilters && (
                <div className="hs-filters">
                  <div className="hs-field hs-field-wide">
                    <label>Datum avser</label>
                    <div className="hs-segment">
                      <button type="button" className={filters.dateMode === "changed" ? "on" : ""} onClick={() => setFilter({ dateMode: "changed" })}>När ändringen gjordes</button>
                      <button type="button" className={filters.dateMode === "shift" ? "on" : ""} onClick={() => setFilter({ dateMode: "shift" })}>Passets datum</button>
                    </div>
                  </div>

                  <div className="hs-field">
                    <label htmlFor="hs-from">Från</label>
                    <input id="hs-from" type="date" value={filters.from} onChange={(e) => setFilter({ from: e.target.value })} />
                  </div>
                  <div className="hs-field">
                    <label htmlFor="hs-to">Till</label>
                    <input id="hs-to" type="date" value={filters.to} onChange={(e) => setFilter({ to: e.target.value })} />
                  </div>
                  <div className="hs-presets">
                    <button type="button" onClick={() => preset(0)}>Idag</button>
                    <button type="button" onClick={() => preset(6)}>7 dagar</button>
                    <button type="button" onClick={() => preset(29)}>30 dagar</button>
                  </div>

                  <div className="hs-field">
                    <label htmlFor="hs-by">Ändrat av</label>
                    <select id="hs-by" value={filters.performedBy} onChange={(e) => setFilter({ performedBy: e.target.value })}>
                      <option value="">Alla</option>
                      {employeeList.map((e) => <option key={e._id} value={e._id}>{e.name}</option>)}
                    </select>
                  </div>
                  <div className="hs-field">
                    <label htmlFor="hs-emp">Berör person</label>
                    <select id="hs-emp" value={filters.employeeId} onChange={(e) => setFilter({ employeeId: e.target.value })}>
                      <option value="">Alla</option>
                      {employeeList.map((e) => <option key={e._id} value={e._id}>{e.name}</option>)}
                    </select>
                  </div>
                  <div className="hs-field">
                    <label htmlFor="hs-proj">Projekt</label>
                    <select id="hs-proj" value={filters.projectId} onChange={(e) => setFilter({ projectId: e.target.value })}>
                      <option value="">Alla</option>
                      {projectList.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="hs-field">
                    <label htmlFor="hs-schema">Tidsschema</label>
                    <select id="hs-schema" value={filters.timeSchemaId} onChange={(e) => setFilter({ timeSchemaId: e.target.value })}>
                      <option value="">Alla</option>
                      {schemaList.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div className="hs-field">
                    <label htmlFor="hs-action">Typ av ändring</label>
                    <select id="hs-action" value={filters.action} onChange={(e) => setFilter({ action: e.target.value })}>
                      <option value="">Alla</option>
                      {ACTION_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {anythingActive && (
                <div className="hs-activebar">
                  <span>{loading ? "Söker…" : `${records.length}${hasMore ? "+" : ""} ${records.length === 1 ? "träff" : "träffar"}`}</span>
                  <button type="button" className="hs-link" onClick={clearAll}>Rensa sökning och filter</button>
                </div>
              )}
            </div>

            {/* Lista */}
            {error && <p className="hs-error">{error}</p>}

            {loading && records.length === 0 ? (
              <p className="hs-muted hs-center">Laddar historik…</p>
            ) : records.length === 0 && !error ? (
              <div className="hs-card hs-empty">
                <strong>{anythingActive ? "Inga ändringar hittades" : "Ingen historik än"}</strong>
                <span>
                  {anythingActive
                    ? "Prova att ändra sökningen eller ta bort något filter."
                    : "Ändringar i schemat som görs härifrån och framåt visas här."}
                </span>
              </div>
            ) : (
              <div className={loading ? "hs-dim" : ""}>
                {groups.map((g) => (
                  <section key={g.label} className="hs-day">
                    <h3 className="hs-day-label">{g.label}</h3>
                    <div className="hs-card hs-list">
                      {g.items.map((r) => (
                        <article className="hs-row" key={r.id}>
                          <div className="hs-row-time">{timeOf(r.createdAt)}</div>
                          <div className="hs-row-main">
                            <RecordRow r={r} expanded={expandedRecords[r.id]} onOpenShift={setOpenShiftId} onShowAll={showAll} />
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                ))}

                {hasMore && (
                  <div className="hs-center">
                    <button type="button" className="hs-more" onClick={() => load({ append: true, beforeId: nextBeforeId })} disabled={loadingMore}>
                      {loadingMore ? "Hämtar…" : "Visa fler"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {openShiftId && <ShiftDrawer shiftId={openShiftId} user={user} company={company} onClose={() => setOpenShiftId(null)} />}
    </div>
  );
};

export default History;
